import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useContactAvatarUrl } from "@/hooks/useContactAvatars";
import {
  CONTACT_AVATAR_BUCKET,
  CONTACT_AVATAR_TYPES,
  contactAvatarFileError,
  contactAvatarPath,
  resizeContactAvatar,
} from "@/lib/contactAvatar";

interface ContactPhotoProps {
  businessId: string;
  /** CRM record, when the profile has one. */
  customerId: string | null;
  /** Customer Graph contact, when resolved. */
  contactId: string | null;
  initial: string;
  name: string;
}

type StoredPaths = { customer: string | null; contact: string | null };

/**
 * The profile avatar. Click it to upload, change or remove the contact's
 * photo. The same file is saved on the CRM record and on the graph contact so
 * the profile and the Inbox show the same photo.
 */
export default function ContactPhoto({ businessId, customerId, contactId, initial, name }: ContactPhotoProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const queryKey = ["contact-avatar-path", businessId, customerId, contactId] as const;

  const { data: stored = { customer: null, contact: null } } = useQuery<StoredPaths>({
    queryKey,
    queryFn: async () => {
      const [customerRow, contactRow] = await Promise.all([
        customerId
          ? (supabase as any).from("customers").select("avatar_path")
            .eq("business_id", businessId).eq("customer_id", customerId).maybeSingle()
          : Promise.resolve({ data: null }),
        contactId
          ? (supabase as any).from("contacts").select("avatar_path")
            .eq("business_id", businessId).eq("contact_id", contactId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      return {
        customer: customerRow?.data?.avatar_path ?? null,
        contact: contactRow?.data?.avatar_path ?? null,
      };
    },
  });
  const currentPath = stored.customer ?? stored.contact;
  const photoUrl = useContactAvatarUrl(currentPath);

  // Write the path to every record this person has. Succeeds if at least one
  // record took it.
  const savePath = async (path: string | null) => {
    const writes = [
      customerId
        ? (supabase as any).from("customers").update({ avatar_path: path })
          .eq("business_id", businessId).eq("customer_id", customerId)
        : null,
      contactId
        ? (supabase as any).from("contacts").update({ avatar_path: path })
          .eq("business_id", businessId).eq("contact_id", contactId)
        : null,
    ].filter(Boolean);
    const results = await Promise.all(writes);
    const failed = results.filter((result: { error: unknown }) => result.error);
    if (failed.length === results.length) {
      throw new Error("The photo could not be saved");
    }
  };

  const removeOldFiles = async (keep: string | null) => {
    const old = [...new Set([stored.customer, stored.contact])]
      .filter((path): path is string => !!path && path !== keep);
    if (old.length > 0) await supabase.storage.from(CONTACT_AVATAR_BUCKET).remove(old);
  };

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["contact-avatar-path", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["contact-avatar-urls", businessId] }),
    ]);
  };

  const upload = async (file: File) => {
    const problem = contactAvatarFileError(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy(true);
    const path = contactAvatarPath(businessId);
    try {
      const photo = await resizeContactAvatar(file);
      const { error: uploadError } = await supabase.storage
        .from(CONTACT_AVATAR_BUCKET)
        .upload(path, photo, { contentType: "image/jpeg" });
      if (uploadError) throw uploadError;
      try {
        await savePath(path);
      } catch (error) {
        await supabase.storage.from(CONTACT_AVATAR_BUCKET).remove([path]);
        throw error;
      }
      await removeOldFiles(path);
      await refresh();
      toast.success("Photo updated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "The photo could not be saved");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await savePath(null);
      await removeOldFiles(null);
      await refresh();
      toast.success("Photo removed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "The photo could not be removed");
    } finally {
      setBusy(false);
    }
  };

  const canEdit = !!customerId || !!contactId;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={CONTACT_AVATAR_TYPES.join(",")}
        className="hidden"
        data-testid="contact-photo-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void upload(file);
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={!canEdit || busy}>
          <button
            type="button"
            className="group relative shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={currentPath ? `Change photo of ${name}` : `Add a photo of ${name}`}
          >
            <Avatar className="h-12 w-12">
              {photoUrl && <AvatarImage src={photoUrl} alt="" className="object-cover" />}
              <AvatarFallback className="bg-primary text-lg font-heading font-semibold text-primary-foreground">
                {initial}
              </AvatarFallback>
            </Avatar>
            {busy ? (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-foreground/40 text-background">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : canEdit && (
              <span
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center rounded-full bg-foreground/40 text-background opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              >
                <Camera className="h-4 w-4" />
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => inputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            {currentPath ? "Change photo" : "Upload photo"}
          </DropdownMenuItem>
          {currentPath && (
            <DropdownMenuItem onClick={() => void remove()} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Remove photo
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
